import {
  BlockType,
  ColorSchemeSchema,
  FieldGroupSchema,
  FieldRole,
  ImageFieldSchema,
  RichTextFieldSchema,
  UrlFieldSchema,
} from './Types';
import { z } from 'zod';

export enum CTALayout {
  Left = 'left',
  Center = 'center',
}

const CTALayoutSchema = z.nativeEnum(CTALayout).or(z.literal('standard'));
export const CTAUrlFieldSchema = UrlFieldSchema.extend({
  name: z.literal('ctaUrl'),
  role: z.literal(FieldRole.CtaUrl),
});
export type CTAUrlField = z.infer<typeof CTAUrlFieldSchema>;

export const CTAImageFieldSchema = ImageFieldSchema.extend({
  name: z.literal('ctaImage'),
  role: z.literal(FieldRole.Image),
});
export type CTAImageField = z.infer<typeof CTAImageFieldSchema>;

const CTATextFieldSchema = RichTextFieldSchema.extend({
  name: z.literal('ctaText'),
  role: z.literal(FieldRole.CtaText),
});

export const CTAPreheaderFieldSchema = RichTextFieldSchema.extend({
  name: z.literal('preheader'),
  role: z.literal(FieldRole.Preheader),
});
export type CTAPreheaderField = z.infer<typeof CTAPreheaderFieldSchema>;

export const CTAHeaderFieldSchema = RichTextFieldSchema.extend({
  name: z.literal('header'),
  role: z.literal(FieldRole.Header),
});
export type CTAHeaderField = z.infer<typeof CTAHeaderFieldSchema>;

export const CTASubheaderFieldSchema = RichTextFieldSchema.extend({
  name: z.literal('subheader'),
  role: z.literal(FieldRole.Subheader),
});
export type CTASubheaderField = z.infer<typeof CTASubheaderFieldSchema>;

const CTAFieldSchema = z.discriminatedUnion('name', [
  CTAImageFieldSchema,
  CTATextFieldSchema,
  CTAUrlFieldSchema,
  CTAPreheaderFieldSchema,
  CTAHeaderFieldSchema,
  CTASubheaderFieldSchema,
]);

const CTAFieldGroup = FieldGroupSchema.merge(
  z.object({
    fields: z.array(CTAFieldSchema),
  }),
);

const CTAOptionsSchema = z.object({
  largeFormatEnabled: z.boolean().optional(),
  preheaderEnabled: z.boolean().optional(),
  subheaderEnabled: z.boolean().optional(),
  style: z
    .object({
      backgroundPosition: z.string().optional(),
      backgroundImage: z.string().optional(),
      backgroundColor: z.string().optional(),
    })
    .optional(),
  innerStyle: z
    .object({
      backgroundColor: z.string().optional(),
      borderRadius: z.string().optional(),
    })
    .optional(),
  colorScheme: ColorSchemeSchema.optional(),
  layout: CTALayoutSchema.optional(),
});

export const CTABlockConfigSchema = z.object({
  options: CTAOptionsSchema,
  type: z.literal(BlockType.Cta),
  fieldGroups: z.tuple([CTAFieldGroup]).rest(CTAFieldGroup),
  version: z.literal(1),
});
